/**
 * Resource tracking for the three-dimensional viewer.
 *
 * Three.js geometries, materials and textures hold GPU memory that garbage
 * collection does not reclaim: they have to be disposed explicitly. A viewer
 * that rebuilds its scene every time a roof module changes will leak steadily
 * through a working session unless every allocation is accounted for.
 *
 * Registering allocations here rather than disposing them ad hoc makes the
 * accounting testable: a test can build a scene, dispose it, and assert that
 * nothing was left behind, which is not something a leak can be caught by
 * inspection.
 */

import type { BufferGeometry, Material, Object3D, Texture } from 'three';

export interface Disposable {
  dispose(): void;
}

/** A registry of everything a scene allocated, and whether it was released. */
export class ResourceRegistry {
  private readonly tracked = new Set<Disposable>();
  private disposedCount = 0;

  /** Registers a resource and returns it, so it can wrap an allocation. */
  track<T extends Disposable>(resource: T): T {
    this.tracked.add(resource);
    return resource;
  }

  /** Registers several resources at once. */
  trackAll<T extends Disposable>(resources: readonly T[]): readonly T[] {
    for (const resource of resources) this.track(resource);
    return resources;
  }

  /** Number of resources still held. */
  get size(): number {
    return this.tracked.size;
  }

  /** Number of resources released since the registry was created. */
  get released(): number {
    return this.disposedCount;
  }

  /**
   * Disposes everything and empties the registry.
   *
   * A resource that throws while disposing must not stop the rest from being
   * released: one bad material would otherwise leak everything after it.
   */
  disposeAll(): void {
    for (const resource of this.tracked) {
      try {
        resource.dispose();
        this.disposedCount += 1;
      } catch {
        // Continue: releasing the remainder matters more than this failure.
      }
    }
    this.tracked.clear();
  }
}

/**
 * Removes an object from its parent and registers its geometry and materials.
 *
 * Three.js does not dispose the contents of a subtree when it is removed, so
 * detaching a mesh without this leaves its buffers allocated.
 */
export function collectDisposables(root: Object3D): Disposable[] {
  const found: Disposable[] = [];
  root.traverse((node) => {
    const candidate = node as Object3D & {
      geometry?: BufferGeometry;
      material?: Material | Material[];
    };
    if (candidate.geometry) found.push(candidate.geometry);
    if (Array.isArray(candidate.material)) {
      for (const material of candidate.material) {
        found.push(material);
        found.push(...texturesOf(material));
      }
    } else if (candidate.material) {
      found.push(candidate.material);
      found.push(...texturesOf(candidate.material));
    }
  });
  return found;
}

/** Textures referenced by a material, which are separately allocated. */
function texturesOf(material: Material): Texture[] {
  const textures: Texture[] = [];
  for (const value of Object.values(material as unknown as Record<string, unknown>)) {
    if (value && typeof value === 'object' && 'isTexture' in value && (value as Texture).isTexture) {
      textures.push(value as Texture);
    }
  }
  return textures;
}

/** Detaches a subtree from its parent and disposes everything it held. */
export function disposeSubtree(root: Object3D): number {
  const disposables = collectDisposables(root);
  root.removeFromParent();
  let released = 0;
  for (const resource of disposables) {
    try {
      resource.dispose();
      released += 1;
    } catch {
      // As above: one failure must not strand the rest.
    }
  }
  return released;
}
